// QuestionCard.jsx - Güncellenmiş versiyon
import React from 'react';
import { Card, CardContent, Box, Typography, Chip, IconButton, Grid, Paper } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';

const isEmergencyContact = (q) =>
  q?.questionType === "EMERGENCY_CONTACT" ||
  (q?.questionType === "TEXT" && q?.questionText?.startsWith("__EMERGENCY_CONTACT__"));

const getDisplayText = (q) =>
  q?.questionText?.replace(/^__EMERGENCY_CONTACT__\n?/, "") || "";

const QuestionCard = ({ question, onEdit, onDelete }) => {
  const effectiveType = isEmergencyContact(question) ? "EMERGENCY_CONTACT" : question.questionType;

  const questionTypes = [
    { value: "MULTIPLE_CHOICE", label: "Multiple Choice" },
    { value: "SCALE", label: "Scale" },
    { value: "TEXT", label: "Text" },
    { value: "YES_NO", label: "Yes/No" },
    { value: "EMERGENCY_CONTACT", label: "Emergency Contact" }
  ];

  const getQuestionTypeColor = (type) => {
    switch (type) {
      case 'MULTIPLE_CHOICE': return 'primary';
      case 'YES_NO': return 'success';
      case 'SCALE': return 'info';
      case 'TEXT': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            {/* Question Header */}
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Typography variant="h6" fontWeight="600">
                Question {question.questionNumber}
              </Typography>
              <Chip
                label={questionTypes.find(t => t.value === effectiveType)?.label || effectiveType}
                size="small"
                color={getQuestionTypeColor(effectiveType)}
                variant="outlined"
              />
            </Box>

            {/* Question Text */}
            <Typography variant="body1" mb={2} sx={{ lineHeight: 1.6 }}>
              {getDisplayText(question)}
            </Typography>

            {/* Multiple Choice Options */}
            {effectiveType === 'MULTIPLE_CHOICE' && question.options && question.options.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" fontWeight="600" mb={2} color="primary">
                  Multiple Choice Options:
                </Typography>
                <Grid container spacing={1}>
                  {question.options.map((option, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          p: 1.5, 
                          bgcolor: "grey.50",
                          border: '1px solid',
                          borderColor: 'grey.200'
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Typography variant="body2">
                            <strong>{String.fromCharCode(65 + idx)})</strong> {option.text}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            
            {/* Yes/No Options */}
            {effectiveType === 'YES_NO' && question.options && question.options.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" fontWeight="600" mb={2} color="success.main">
                  Yes/No Options:
                </Typography>
                <Grid container spacing={1}>
                  {question.options.map((option, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          p: 1.5, 
                          bgcolor: option.value === 1 ? "success.light" : "grey.100",
                          border: '1px solid',
                          borderColor: option.value === 1 ? 'success.main' : 'grey.300'
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Typography variant="body2" sx={{ 
                            color: option.value === 1 ? 'white' : 'text.primary',
                            fontWeight: option.value === 1 ? 'bold' : 'normal'
                          }}>
                            <strong>{String.fromCharCode(65 + idx)})</strong> {option.text}
                          </Typography>
                          <Chip 
                            label={`${option.value} pt${option.value !== 1 ? 's' : ''}`} 
                            size="small" 
                            sx={{ 
                              bgcolor: option.value === 1 ? 'success.dark' : 'grey.400', 
                              color: 'white',
                              fontWeight: 'bold',
                              minWidth: '45px'
                            }} 
                          />
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            
            {/* Scale Information */}
            {effectiveType === "SCALE" && (
              <Box mt={2}>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    bgcolor: "info.light",
                    border: '1px solid',
                    borderColor: 'info.main'
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="600" color="info.dark" mb={1}>
                    Scale Configuration
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Range:</strong> {question.minScale} - {question.maxScale}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Total Points:</strong> {question.maxScale - question.minScale + 1} possible values
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Labels:</strong> "{question.minLabel}" ↔ "{question.maxLabel}"
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  {/* Scale Preview */}
                  <Box mt={2}>
                    <Typography variant="body2" fontWeight="600" mb={1}>Preview:</Typography>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography variant="caption" sx={{ textAlign: 'left', minWidth: 80 }}>
                        {question.minLabel || `${question.minScale} (Min)`}
                      </Typography>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {Array.from({ length: (question.maxScale - question.minScale + 1) }, (_, i) => {
                          const value = question.minScale + i;
                          return (
                            <Box 
                              key={value} 
                              sx={{ 
                                width: 24, 
                                height: 24, 
                                borderRadius: '50%', 
                                bgcolor: 'info.main', 
                                color: 'white', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                fontSize: '0.7rem',
                                fontWeight: 'bold'
                              }}
                            >
                              {value}
                            </Box>
                          );
                        })}
                      </Box>
                      <Typography variant="caption" sx={{ textAlign: 'right', minWidth: 80 }}>
                        {question.maxLabel || `${question.maxScale} (Max)`}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            )}
            
            {/* Emergency Contact Information */}
            {effectiveType === "EMERGENCY_CONTACT" && (
              <Box mt={2}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: "error.light",
                    border: '1px solid',
                    borderColor: 'error.main'
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="600" color="error.dark" mb={1}>
                    Emergency Contact Form
                  </Typography>
                  <Typography variant="body2" color="error.dark" mb={1}>
                    Full Name: ___________________________
                  </Typography>
                  <Typography variant="body2" color="error.dark" mb={1}>
                    Relationship to you: __________________
                  </Typography>
                  <Typography variant="body2" color="error.dark" mb={1}>
                    Phone Number: ________________________
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 1 }}>
                    <Box component="span" sx={{ border: "1px solid", borderColor: "error.dark", width: 14, height: 14, flexShrink: 0, mt: "2px" }} />
                    <Typography variant="body2" color="error.dark">
                      I understand this contact will only be used in an emergency situation involving risk of harm.
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            )}

            {/* Text Question Information */}
            {effectiveType === "TEXT" && (
              <Box mt={2}>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    bgcolor: "warning.light",
                    border: '1px solid',
                    borderColor: 'warning.main'
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="600" color="warning.dark" mb={1}>
                    Open-ended Text Question
                  </Typography>
                  <Typography variant="body2" color="warning.dark">
                    <strong>Response Type:</strong> Free text input<br/>
                    <strong>Scoring:</strong> Manual review or AI-assisted analysis required
                  </Typography>
                </Paper>
              </Box>
            )}
          </Box>
          
          {/* Action Buttons */}
          <Box sx={{ ml: 2 }}>
            <IconButton 
              size="small" 
              color="primary"
              onClick={onEdit}
              sx={{ mb: 1 }}
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              size="small" 
              color="error"
              onClick={onDelete}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default QuestionCard;
